import axios from 'axios';
import { API_BASE_URL } from '../config';

// Create axios instance with base configuration
const api = axios.create({
    // baseURL: process.env.REACT_APP_API_URL || '/api',
  baseURL: API_BASE_URL,
  // baseURL: API_BASE_URL,

  
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log(`Adding token to ${config.url}`);
    } else {
      console.warn(`No token found for request to ${config.url}`);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect to login if we're not already on login page or trying to login
      // This prevents infinite redirects and page refreshes
      const isLoginPage = window.location.pathname === '/login';
      const isLoginAttempt = error.config.url?.includes('/auth/login');
      
      if (!isLoginPage && !isLoginAttempt && !error.config.url?.includes('/user/profile')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth Services
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  forgotPassword: (email) => api.post('/otp/send-password-reset', { email }),
  resetPassword: (email, newPassword,confirmPassword, role) => api.post('/auth/reset-password', { email, newPassword, confirmPassword, role }),
  resendOtp: (email,otpType) => api.post('/otp/can-resend', { email, otpType }),
  verifyOTP: (email, otp,otpType,role) => api.post('/otp/verify', { email, otp ,otpType,role}),
  getProfile: () => api.get('/user/profile'),
  updateProfile: (userData) => api.put('/user/profile', userData),
};

// Platform Services
export const platformAPI = {
  getPlatforms: (params) => api.get('/platforms', { params }),
  getPlatform: (id) => api.get(`/platforms/${id}`),
  createPlatform: (platformData) => api.post('/platforms', platformData),
  updatePlatform: (id, platformData) => api.put(`/platforms/${id}`, platformData),
  deletePlatform: (id) => api.delete(`/platforms/${id}`),
};

// Brand Services
export const brandAPI = {
  getBrands: (params) => api.get('/brands', { params }),
  getBrand: (id) => api.get(`/brands/${id}`),
  createBrand: (brandData) => api.post('/brands', brandData),
  updateBrand: (id, brandData) => api.put(`/brands/${id}`, brandData),
  deleteBrand: (id) => api.delete(`/brands/${id}`),
  getActiveBrands: () => api.get('/brands/active'),
  searchBrands: (term) => api.get(`/brands/search?term=${term}`),
};

// Product Services
export const productAPI = {
  getProducts: (params) => api.post('/products/search', { params }),
  getProduct: (id) => api.get(`/products/${id}`),
  getActiveProducts: () => api.get('/products/active'),

  createProduct: (productData) => {
    if (productData instanceof FormData) {
      return api.post('/products', productData, {
        headers: {
          'Content-Type': undefined
        },
        transformRequest: [(data, headers) => {
          delete headers['Content-Type'];
          return data;
        }]
      });
    }
    return api.post('/products', productData);
  },
  updateProduct: (id, productData) => {
    if (productData instanceof FormData) {
      return api.put(`/products/${id}`, productData, {
        headers: {
          'Content-Type': undefined
        },
        transformRequest: [(data, headers) => {
          delete headers['Content-Type'];
          return data;
        }]
      });
    }
    return api.put(`/products/${id}`, productData);
  },
  deleteProduct: (id) => api.delete(`/products/${id}`),
  getActiveDeals: () => api.get('/products/active-deals'),
  searchProducts: (searchRequest) => api.post('/products/search', searchRequest),
  toggleProductStatus: (id) => api.patch(`/products/${id}/toggle-status`),
  getProductStats: () => api.get('/products/stats'),
  getProductsByBrands: (brandIds) => api.post('/products/by-brands', brandIds),
  getMediatorAssignedProducts: (mediatorId) => api.get(`/mediator-products/mediator/${mediatorId}/assigned`),
  getMediatorAvailableProducts: (mediatorId) => api.get(`/mediator-products/mediator/${mediatorId}/available`),
  requestDeal: (productId, mediatorId) => api.post(`/deal-requests/request?productId=${productId}&mediatorId=${mediatorId}`),
  getPendingDealRequests: () => api.get('/deal-requests/pending'),
  updateDealRequestStatus: (requestId, status, adminNote) => 
    api.patch(`/deal-requests/${requestId}/status?status=${status}&adminNote=${encodeURIComponent(adminNote)}`),
};

// Order Services
export const orderAPI = {
  createOrder: (formData) => {
    // Ensure FormData is properly handled for multipart uploads
    if (!(formData instanceof FormData)) {
      console.error('Expected FormData but received:', typeof formData);
      throw new Error('FormData required for order creation');
    }
    
    console.log('=== orderAPI.createOrder ===');
    console.log('FormData instance:', formData instanceof FormData);
    console.log('FormData entries:', formData.entries().length);
    
    // Create custom config to ensure multipart handling
    const config = {
      headers: {
        // Don't set Content-Type - let Axios set it automatically with boundary
        'Content-Type': undefined
      },
      // Ensure Axios doesn't transform FormData
      transformRequest: [(data, headers) => {
        // Delete the Content-Type header to let browser set it with boundary
        delete headers['Content-Type'];
        return data;
      }]
    };
    
    console.log('Making POST request to /orders with FormData');
    return api.post('/orders', formData, config);
  },
  updateOrderForm: (id, formData) => {
    // This endpoint now consumes MULTIPART_FORM_DATA
    const config = {
      headers: {
        'Content-Type': undefined
      },
      transformRequest: [(data, headers) => {
        delete headers['Content-Type'];
        return data;
      }]
    };
    return api.put(`/orders/${id}`, formData, config);
  },
  getOrders: (params) => api.get('/orders', { params }),
  getOrder: (id) => api.get(`/orders/${encodeURIComponent(String(id || '').trim())}`),
  deleteOrder: (id) => api.delete(`/orders/${encodeURIComponent(String(id || '').trim())}`),
  updateOrder: (id, orderData) => api.put(`/orders/${encodeURIComponent(String(id || '').trim())}`, orderData),
  searchOrders: (searchRequest) => api.post('/orders/search', searchRequest),
  uploadOrderScreenshot: (orderId, file) => {
    const formData = new FormData();
    formData.append('screenshot', file);
    return api.post(`/orders/${orderId}/screenshot`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  submitRefundForm: (orderId, refundData) => api.post(`/orders/${orderId}/refund`, refundData),
  uploadRefundScreenshots: (orderId, files) => {
    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`screenshots`, file);
    });
    return api.post(`/orders/${orderId}/refund-screenshots`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  trackOrder: (orderId) => api.get(`/orders/${orderId}/track`),
};

// Mediator Services
export const mediatorAPI = {
  getMediators: (params) => api.get('/mediators', { params }),
  getMediatorsByProduct: (productId, feedbackType) =>
      api.get(`/mediator-products/${productId}`, {
        params: feedbackType ? { feedbackType } : undefined
      }),
  getMediatorByEmail: (email) => api.get(`/mediators/email/${email}`),
  getAllMediators: () => api.get('/mediators'),
  searchMediators: (term, page = 0, size = 10) => api.get(`/mediators/search?term=${term}&page=${page}&size=${size}`, {
    headers: { 'Content-Type': 'application/json' }
  }),
  getMediator: (id) => api.get(`/mediators/${id}`),
  createMediator: (mediatorData) => api.post('/mediators', mediatorData),
  updateMediator: (id, mediatorData) => api.put(`/mediators/${id}`, mediatorData),
  deleteMediator: (id) => api.delete(`/mediators/${id}`),
  toggleMediatorStatus: (id) => api.patch(`/mediators/${id}/toggle-status`),
  getMediatorOrders: (id, params) => api.get(`/mediators/${id}/orders`, { params }),
  getMediatorDashboardStats: (id) => api.get(`/mediator-products/mediator/${id}/dashboard-stats`),
  requestPayout: (id) => api.post(`/mediators/${id}/request-payout`),
  markOrdersPaid: (id, orderIds) => api.post(`/mediators/${id}/mark-paid`, { orderIds }),
  exportOrders: (id, params) => api.get(`/mediators/${id}/export`, { 
    params,
    responseType: 'blob'
  }),
  getRewardSettings: () => api.get('/mediator/rewards/settings'),
  getRewardWallet: () => api.get('/mediator/rewards/wallet'),
  getRewardTransactions: (params) => api.get('/mediator/rewards/transactions', { params }),
};

// Admin Services
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getPenaltyOrders: (page = 0, size = 20) => api.get('/admin/penalties', { params: { page, size } }),
  searchOrders: (params) => api.get('/orders/search', { params }),
  getBuyerStats: (email) => api.get(`/orders/buyer-stats?email=${email}`),
  updateOrderStatus: (id, status, reason = '') => api.patch(`/orders/${id}/status`, null, { 
    params: { status, reason } 
  }),
  getNotifications: () => api.get('/notifications'),
  getUnreadNotificationCount: () => api.get('/notifications/unread-count'),
  markNotificationRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllNotificationsRead: () => api.patch('/notifications/read-all'),
  sendNotification: (notificationData) => api.post('/admin/notifications', notificationData),
  getDealRequests: () => api.get('/admin/deal-requests'),
  updateDealRequest: (id, status) => api.put(`/admin/deal-requests/${id}`, { status }),
  unlockMediator: (id) => api.patch(`/admin/mediators/${id}/unlock`),
  unlockUser: (id) => api.patch(`/admin/users/${id}/unlock`),
  searchUsers: (term, page = 0, size = 10, role) =>
    api.get('/admin/users', { params: { term, page, size, role } }),
  getUsers: (params) => api.get('/admin/users', { params }),
  createUser: (userData) => api.post('/admin/users', userData),
  updateUser: (id, userData) => api.put(`/admin/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  getRefundPolicySettings: () => api.get('/admin/refund-policy/settings'),
  updateRefundPolicySettings: (data) => api.put('/admin/refund-policy/settings', data),
  getMediatorRewardSettings: () => api.get('/admin/mediator-rewards/settings'),
  updateMediatorRewardSettings: (data) => api.put('/admin/mediator-rewards/settings', data),
  getMediatorRewardWallets: () => api.get('/admin/mediator-rewards/wallets'),
  getMediatorRewardWallet: (mediatorId) => api.get(`/admin/mediator-rewards/wallets/${mediatorId}`),
  payMediatorReward: (mediatorId, data) => api.post(`/admin/mediator-rewards/wallets/${mediatorId}/pay`, data),
  getMediatorRewardTransactions: (params) => api.get('/admin/mediator-rewards/transactions', { params }),
  getMediatorRewardTransactionsByMediator: (mediatorId, params) =>
    api.get(`/admin/mediator-rewards/transactions/${mediatorId}`, { params }),
};

export const refundPolicyAPI = {
  getSettings: () => api.get('/refund-policy/settings'),
};

export const notificationAPI = {
  getAll: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.patch('/notifications/read-all'),
};

// Sheet Services
export const sheetAPI = {
  getSheets: () => api.get('/sheets/list'),
  syncSheet: (spreadsheetId) => api.get(`/sheets/sync/${spreadsheetId}`),
  deleteSheet: (spreadsheetId) => api.delete(`/sheets/${spreadsheetId}`),
  generateSheet: (filterDto) => api.post('/sheets/generate', filterDto, { timeout: 180000 }),
};

// Mediator Product Services
export const mediatorProductAPI = {
  assignProduct: (data) => api.post('/mediator-products', data),
  searchAssignments: (request) => api.post('/mediator-products/search', request),
  deleteAssignment: (id) => api.delete(`/mediator-products/${id}`),
};

// Support Services
export const supportAPI = {
  createTicket: (ticketData) => api.post('/support/tickets', ticketData),
  getTickets: (params) => api.get('/support/tickets', { params }),
  getTicket: (id) => api.get(`/support/tickets/${id}`),
  updateTicket: (id, ticketData) => api.put(`/support/tickets/${id}`, ticketData),
  addTicketMessage: (id, message) => api.post(`/support/tickets/${id}/messages`, { message }),
};

// Enum Services
export const enumAPI = {
  // Order Status
  getOrderStatus: () => api.get('/enums/OrderStatus'),
  
  // Deal Feedback Types
  getDealFeedbackType: () => api.get('/enums/DealFeedbackType'),
  
  // Deal Order Types
  getDealOrderType: () => api.get('/enums/DealOrderType'),
  
  // User Roles
  getUserRole: () => api.get('/enums/UserRole'),
  
  // Payment Methods
  getPaymentMethod: () => api.get('/enums/PaymentMethod'),
  
  // Order Status for Admin
  getAdminOrderStatus: () => api.get('/enums/OrderStatus'),
  
  // Payout Status
  getPayoutStatus: () => api.get('/enums/PayoutStatus'),
  
  // Notification Types
  getNotificationType: () => api.get('/enums/NotificationType'),
  
  // Ticket Status
  getTicketStatus: () => api.get('/enums/TicketStatus'),
  
  // Ticket Priority
  getTicketPriority: () => api.get('/enums/TicketPriority'),
  
  // Generic method to get any enum
  getEnum: (enumName) => api.get(`/enums/${enumName}`),
  
  // Get all commonly used enums in one call
  getCommonEnums: () => api.get('/enums/common'),
};

// Utility Services
export const utilityAPI = {
  uploadFile: (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  sendWhatsApp: (phoneNumber, message) => api.post('/whatsapp/send', { phoneNumber, message }),
  generateReport: (type, params) => api.get(`/reports/${type}`, { 
    params,
    responseType: 'blob'
  }),
};

export default api;
